class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const renderToken = ++FacetFiltersForm.renderTokenCounter;
    FacetFiltersForm.latestRenderToken = renderToken;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    if (!sections.length) {
      FacetFiltersForm.hideLoadingElements();
      return;
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, renderToken)
        : FacetFiltersForm.renderSectionFromFetch(url, event, renderToken);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static hideLoadingElements() {
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    document.getElementById('ProductCount')?.classList.remove('loading');
    document.getElementById('ProductCountDesktop')?.classList.remove('loading');
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
  }

  static isStaleRender(renderToken) {
    return FacetFiltersForm.latestRenderToken !== renderToken;
  }

  static renderSectionFromFetch(url, event, renderToken) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch facets: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        if (FacetFiltersForm.isStaleRender(renderToken)) return;
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch(() => {
        if (FacetFiltersForm.isStaleRender(renderToken)) return;
        FacetFiltersForm.hideLoadingElements();
      });
  }

  static renderSectionFromCache(filterDataUrl, event, renderToken) {
    if (FacetFiltersForm.isStaleRender(renderToken)) return;
    const cachedSection = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cachedSection) return;
    const html = cachedSection.html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const sourceProductGridContainer = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (!productGridContainer || !sourceProductGridContainer) return;

    productGridContainer.innerHTML = sourceProductGridContainer.innerHTML;

    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(productGridContainer);
  }

  static renderProductCount(html) {
    const countDOM = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    if (!countDOM) {
      FacetFiltersForm.hideLoadingElements();
      return;
    }

    const count = countDOM.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    FacetFiltersForm.hideLoadingElements();
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // If the response is missing expected facet containers, keep existing filters to avoid a blank sidebar/drawer.
    const hasFacetFormsInResponse =
      parsedHTML.querySelector('#FacetFiltersForm') ||
      parsedHTML.querySelector('#FacetFiltersFormMobile') ||
      parsedHTML.querySelector('#FacetFiltersPillsForm');
    if (!hasFacetFormsInResponse) return;

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const targetParent = document.getElementById(elementToRender.parentElement.id);
          const firstJsFilter = targetParent ? targetParent.querySelector('.js-filter') : null;
          if (firstJsFilter) {
            firstJsFilter.before(elementToRender);
          } else if (targetParent) {
            targetParent.appendChild(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentActiveFacetsElement = document.querySelector(selector);
      if (!activeFacetsElement || !currentActiveFacetsElement) return;
      currentActiveFacetsElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceElements = html.querySelectorAll(selector);
      const destinationElements = document.querySelectorAll(selector);
      if (!sourceElements.length || !destinationElements.length) return;

      destinationElements.forEach((destinationElement, index) => {
        const sourceElement = sourceElements[index] || sourceElements[0];
        if (!sourceElement) return;
        destinationElement.innerHTML = sourceElement.innerHTML;
      });
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const mobileDrawer = mobileForm ? mobileForm.closest('menu-drawer') : null;
    if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') mobileDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return [];

    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (!(event.target instanceof Element)) return;

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const mobileCheckbox = event.target.closest('input.mobile-facets__checkbox');
    if (mobileCheckbox) {
      const form = mobileCheckbox.closest('form');
      if (!form) return;
      const searchParams = this.createSearchParams(form);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const parentForm = event.target.closest('form');
      if (!parentForm) return;
      const isMobile = parentForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.renderTokenCounter = 0;
FacetFiltersForm.latestRenderToken = 0;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class MobileFacetsProductType extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-mobile-product-type-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();

      const drawer = this.closest('menu-drawer');
      const mainDetails = drawer?.querySelector('.mobile-facets__disclosure');
      const mainSummary = mainDetails?.querySelector(':scope > summary');
      const productTypeDetails = drawer?.querySelector('[data-mobile-product-type-filter]');
      const productTypeSummary = productTypeDetails?.querySelector(':scope > summary');

      if (!mainDetails || !mainSummary || !productTypeDetails || !productTypeSummary) return;

      if (!mainDetails.open) mainSummary.click();

      window.requestAnimationFrame(() => {
        if (!productTypeDetails.open) productTypeSummary.click();
      });
    });
  }
}

customElements.define('mobile-facets-product-type', MobileFacetsProductType);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
